import ImageUploader from "components/ImageUploader";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { AnimatePresence, motion } from "framer-motion";
import {
  Clock,
  DollarSign,
  Edit2,
  Filter,
  Image,
  Plus,
  Search,
  Trash2,
} from "lucide-react";
import { useLayoutEffect, useState } from "react";
import { toast } from "sonner";
import * as Yup from "yup";
import { useRemoteService } from "../services/authService";
import { logDev, loggedInUserId } from "../services/CommonService";

interface Service {
  id: number;
  name: string;
  description: string;
  duration: number;
  price: number;
  category: string | null;
  image: string;
  created_on: string | null;
  status: string | null;
}

interface Category {
  id: string;
  name: string;
  description: string;
  created_on: string;
  status: string;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const serviceValidationSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Name is too short")
    .max(50, "Name is too long")
    .required("Name is required"),
  description: Yup.string()
    .min(10, "Description is too short")
    .max(200, "Description is too long")
    .required("Description is required"),
  duration: Yup.number()
    .min(5, "Duration must be at least 5 minutes")
    .max(480, "Duration cannot exceed 8 hours")
    .required("Duration is required"),
  price: Yup.number()
    .min(0, "Price cannot be negative")
    .required("Price is required"),
  category: Yup.string().required("Category is required"),
  image: Yup.string()
    .required("Image is required")
    .test("is-valid-size", "Image size must be less than 5MB", (value) => {
      if (!value) return true;
      if (value.startsWith("data:image")) {
        const base64Length = value.split(",")[1].length;
        const sizeInBytes = (base64Length * 3) / 4;
        return sizeInBytes <= MAX_FILE_SIZE;
      }
      return true;
    }),
});

const Services = () => {
  const { sendPostToServer, sendGetToServer } = useRemoteService();
  const [services, setServices] = useState<Service[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<number>();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);

  useLayoutEffect(() => {
    let sub = true;

    if (sub) {
      fetchCategories();
      fetchServices();
    }

    return () => {
      sub = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchServices = async () => {
    try {
      const res = await sendGetToServer("services/get-all");
      if (res.returnObject && Array.isArray(res.returnObject.services)) {
        setServices(res.returnObject.services);
      }
    } catch (error) {
      toast.error("Failed to fetch services");
      console.error(error);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await sendGetToServer("services/fetch-all-categories");
      console.warn("Categories response:", res);
      if (res.returnObject && Array.isArray(res.returnObject.categories)) {
        setCategories(res.returnObject.categories);
      }
    } catch (error) {
      toast.error("Failed to fetch categories");
      console.error(error);
    }
  };

  const filteredServices = services.filter((service: any) => {
    const matchesSearch = service.name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesCategory =
      !selectedCategory || service.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleDelete = async (serviceId: number) => {
    if (window.confirm("Are you sure you want to delete this service?")) {
      try {
        const res = await sendPostToServer(
          "services/delete",
          JSON.stringify({ id: serviceId })
        );

        if (res.returnCode === 200) {
          setServices(services.filter((s) => s.id !== serviceId));
          toast.success("Service deleted successfully");
        } else {
          throw new Error(res.returnMessage || "Failed to delete service");
        }
      } catch (error) {
        toast.error("Failed to delete service");
        console.error(error);
      }
    }
  };

  const handleEdit = (service: Service) => {
    setEditingService(service);
    setIsAddModalOpen(true);
  };

  const handleSave = async (values: Partial<Service>, { resetForm }: any) => {
    try {
      const payload = {
        ...values,
        id: editingService?.id,
        image: values.image,
      };

      logDev("We are reviewing this payload: ", payload);
      const endpoint = editingService
        ? "services/update"
        : `services/create-new-service?createdBy=${loggedInUserId()}`;

      const { returnMessage, returnCode, returnObject } =
        await sendPostToServer(endpoint, JSON.stringify(payload));

      if (returnCode === 200 || returnCode === 201) {
        logDev("Service creation details: ", returnObject);
        if (editingService) {
          setServices(
            services.map((s) =>
              s.id === editingService.id ? { ...s, ...values } : s
            )
          );
          toast.success("Service updated successfully");
        } else {
          setServices([...services, returnObject]);
          toast.success("Service added successfully");
        }
        setIsAddModalOpen(false);
        setEditingService(null);
        resetForm();
      } else {
        throw new Error(returnMessage || "Operation failed");
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to save service");
      console.error(error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900">Services</h1>
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Service
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search services..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-5 w-5 text-gray-400" />
          <select
            value={selectedCategory}
            onChange={(e) => {
              logDev(Number(e.currentTarget.value));
              setSelectedCategory(Number(e.target?.value));
            }}
            className="rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          >
            <option value="">All Categories</option>
            {categories.map((category) => (
              <option key={category.id} value={category.name}>
                {category.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Services Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <AnimatePresence>
          {filteredServices.map((service) => (
            <motion.div
              key={service.id}
              layout
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow"
            >
              {service.image ? (
                <img
                  src={service.image}
                  alt={service.name}
                  className="w-full h-48 object-cover rounded-t-lg"
                />
              ) : (
                <Image size={8} className="w-full h-48 rounded-t-lg" />
              )}
              <div className="p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      {service.name}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {service.description}
                    </p>
                  </div>
                  {service.category && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                      {service.category}
                    </span>
                  )}
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <div className="flex items-center text-gray-500">
                    <Clock className="h-4 w-4 mr-1" />
                    <span className="text-sm">{service.duration} min</span>
                  </div>
                  <div className="flex items-center text-gray-900 font-medium">
                    <DollarSign className="h-4 w-4" />
                    <span>{service.price}</span>
                  </div>
                </div>
                <div className="mt-4 flex justify-end space-x-2">
                  <button
                    onClick={() => handleEdit(service)}
                    className="p-2 text-gray-400 hover:text-gray-500"
                  >
                    <Edit2 className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(service.id)}
                    className="p-2 text-gray-400 hover:text-red-500"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Add/Edit Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              {editingService ? "Edit Service" : "Add New Service"}
            </h2>
            <Formik
              initialValues={
                editingService || {
                  name: "",
                  description: "",
                  duration: 30,
                  price: 0,
                  category: "",
                  image: "",
                }
              }
              validationSchema={serviceValidationSchema}
              onSubmit={handleSave}
            >
              {({
                isSubmitting,
                setFieldValue,
                setFieldTouched,
                errors,
                touched,
              }) => (
                <Form className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Name
                    </label>
                    <Field
                      type="text"
                      name="name"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    />
                    <ErrorMessage
                      name="name"
                      component="div"
                      className="mt-1 text-sm text-red-600"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Description
                    </label>
                    <Field
                      as="textarea"
                      name="description"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    />
                    <ErrorMessage
                      name="description"
                      component="div"
                      className="mt-1 text-sm text-red-600"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Duration (min)
                      </label>
                      <Field
                        type="number"
                        name="duration"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      />
                      <ErrorMessage
                        name="duration"
                        component="div"
                        className="mt-1 text-sm text-red-600"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Price (UGX)
                      </label>
                      <Field
                        type="number"
                        name="price"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      />
                      <ErrorMessage
                        name="price"
                        component="div"
                        className="mt-1 text-sm text-red-600"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Category
                    </label>
                    <Field
                      as="select"
                      name="category"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    >
                      <option value="">Select a category</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage
                      name="category"
                      component="div"
                      className="mt-1 text-sm text-red-600"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Service Image
                    </label>
                    <ImageUploader
                      value={editingService?.image}
                      onChange={(base64) => {
                        setFieldValue("image", base64);
                        setFieldTouched("image", true);
                      }}
                      onBlur={() => setFieldTouched("image", true)}
                      error={
                        touched.image && errors.image ? errors.image : undefined
                      }
                    />
                  </div>

                  <div className="flex justify-end space-x-2">
                    <button
                      type="button"
                      onClick={() => {
                        setIsAddModalOpen(false);
                        setEditingService(null);
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 rounded-md disabled:opacity-50"
                    >
                      {isSubmitting
                        ? "Saving..."
                        : editingService
                        ? "Save Changes"
                        : "Add Service"}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      )}
    </div>
  );
};

export default Services;
